import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface InvitePreview {
  orgId: string;
  orgName: string | null;
  orgKind: string | null;
  scope: string;
  role: string;
  expiresAt: string;
}

export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => apiClient.previewInvitation(token!),
    enabled: !!token,
  });

  const accept = useMutation({
    mutationFn: () => apiClient.acceptInvitation(token!),
    onSuccess: (res) => {
      const data = res.data as { orgId?: string } | undefined;
      queryClient.invalidateQueries({ queryKey: ['me', 'orgs'] });
      if (data?.orgId) {
        try { localStorage.setItem('lastOrgId', data.orgId); } catch { /* ignore */ }
        navigate(`/orgs/${data.orgId}/dashboard`, { replace: true });
      }
    },
  });

  // Auto-accept once we've shown the preview? No — make it a click so the
  // user knows what they're joining.

  useEffect(() => { /* preview only */ }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        <Card className="max-w-md p-8 text-center">
          <p className="text-base font-semibold mb-2">Invitation not found</p>
          <p className="text-sm text-muted-foreground mb-4">
            This invitation link is invalid, expired, or already used.
          </p>
          <Button onClick={() => navigate('/')}>Back to home</Button>
        </Card>
      </div>
    );
  }

  const invite = data.data as InvitePreview;
  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <Card className="max-w-md w-full p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">You've been invited to</p>
            <p className="text-lg font-bold text-foreground">{invite.orgName ?? 'an organization'}</p>
          </div>
        </div>
        <div className="space-y-2 mb-6 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Role</span>
            <span className="font-medium capitalize">{invite.role}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Scope</span>
            <span className="font-medium capitalize">{invite.scope}-level access</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Expires</span>
            <span className="font-medium">{new Date(invite.expiresAt).toLocaleString()}</span>
          </div>
        </div>
        <Button
          onClick={() => accept.mutate()}
          disabled={accept.isPending}
          className="w-full"
        >
          {accept.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
          Accept invitation
        </Button>
        {accept.isError && (
          <p className="text-xs text-red-600 mt-3 text-center">{(accept.error as Error).message}</p>
        )}
      </Card>
    </div>
  );
}
